import * as React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useQuery, useMutation } from "@apollo/client";
import { GET_ADMIN_TAGS } from "../../data/graphql/queries";
import { CREATE_TAG, DELETE_TAG } from "../../data/graphql/mutations";
import { TagType } from "../../data/types";

export default function AdminManageTags() {
  const [newTagName, setNewTagName] = React.useState("");
  const [tagToDelete, setTagToDelete] = React.useState<TagType | null>(null);
  const [successMessage, setSuccessMessage] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");

  const { loading, error, data, refetch } = useQuery(GET_ADMIN_TAGS);

  const [createTag, { loading: createLoading }] = useMutation(CREATE_TAG, {
    onCompleted: () => {
      setNewTagName("");
      setSuccessMessage("Tag created successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      refetch();
    },
    onError: (err) => {
      setErrorMessage(`Error creating tag: ${err.message}`);
      setTimeout(() => setErrorMessage(""), 5000);
    },
  });

  const [deleteTag, { loading: deleteLoading }] = useMutation(DELETE_TAG, {
    onCompleted: () => {
      setTagToDelete(null);
      setSuccessMessage("Tag deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      refetch();
    },
    onError: (err) => {
      setTagToDelete(null);
      setErrorMessage(`Error deleting tag: ${err.message}`);
      setTimeout(() => setErrorMessage(""), 5000);
    },
  });

  const handleCreate = () => {
    if (newTagName.trim()) {
      createTag({ variables: { name: newTagName.trim() } });
    }
  };

  const handleConfirmDelete = () => {
    if (tagToDelete) {
      deleteTag({ variables: { id: tagToDelete.id } });
    }
  };

  const tags: TagType[] = data?.adminTags || [];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Manage Tags
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Create New Tag
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            label="Tag Name"
            variant="outlined"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleCreate()}
            sx={{ flexGrow: 1 }}
            placeholder="Enter tag name..."
          />
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={createLoading || !newTagName.trim()}
            startIcon={createLoading ? <CircularProgress size={16} /> : <AddIcon />}
          >
            Create
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          All Tags ({tags.length})
        </Typography>

        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">Error loading tags: {error.message}</Alert>
        ) : tags.length === 0 ? (
          <Typography sx={{
            color: "text.secondary"
          }}>No tags found.</Typography>
        ) : (
          <List>
            {tags.map((tag: TagType) => (
              <ListItem
                key={tag.id}
                sx={{ border: 1, borderColor: "divider", borderRadius: 1, mb: 1 }}
              >
                <ListItemText
                  primary={tag.name}
                  secondary={`slug: ${tag.slug}`}
                />
                <Chip
                  label={tag.state}
                  color={tag.state === "active" ? "success" : "default"}
                  size="small"
                  sx={{ mr: 2 }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    color="error"
                    onClick={() => setTagToDelete(tag)}
                    disabled={deleteLoading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <Dialog open={!!tagToDelete} onClose={() => setTagToDelete(null)}>
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete &quot;{tagToDelete?.name}&quot;? This will remove it from all publications.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagToDelete(null)}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete} disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={16} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
